import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { colors, spacing, fonts, SERIF } from "@/theme";

export default function Login() {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("driver");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [managerCode, setManagerCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const goHome = (u) => navigate(u.role === "driver" ? "/pending" : "/board", { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await signIn(email.trim(), password);
        goHome(u);
      } else {
        if (!name.trim()) throw new Error("Name required");
        if ((role === "manager" || role === "plant") && !managerCode.trim())
          throw new Error(role === "manager" ? "Manager invite code required" : "Plant user invite code required");
        const u = await signUp(email.trim(), password, name.trim(), role, managerCode.trim() || undefined);
        goHome(u);
      }
    } catch (e2) {
      setErr(e2?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (r) => {
    if (r === "manager") { setEmail("manager@yardops.com"); setPassword("Manager123!"); }
    else if (r === "plant") { setEmail("plant@yardops.com"); setPassword("Plant123!"); }
    else { setEmail("driver@yardops.com"); setPassword("Driver123!"); }
    setMode("login");
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.hero}>
          <img
            src="https://customer-assets.emergentagent.com/job_yard-logistics-hub-1/artifacts/mue4tej5_GSGROUP-LOGO-RGB_MAIN.jpg"
            alt="GS Group"
            style={s.logo}
          />
          <div style={s.brandTag}>GS Group – Yard Management</div>
        </div>

        <form style={s.formWrap} onSubmit={submit}>
          <div style={s.tabs}>
            <button type="button" data-testid="tab-login" onClick={() => setMode("login")}
              style={{ ...s.tab, ...(mode === "login" ? s.tabActive : {}) }}>
              <span style={{ ...s.tabText, color: mode === "login" ? colors.onSurfaceInverse : colors.onSurface }}>Sign in</span>
            </button>
            <button type="button" data-testid="tab-register" onClick={() => setMode("register")}
              style={{ ...s.tab, ...(mode === "register" ? s.tabActive : {}) }}>
              <span style={{ ...s.tabText, color: mode === "register" ? colors.onSurfaceInverse : colors.onSurface }}>Register</span>
            </button>
          </div>

          {mode === "register" && (
            <div>
              <div style={s.label}>Full name</div>
              <input data-testid="input-name" style={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="John Driver" />
              <div style={s.label}>Role</div>
              <div style={s.roleRow}>
                {["driver", "plant", "manager"].map((r) => {
                  const active = role === r;
                  return (
                    <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setRole(r)}
                      style={{ ...s.roleBtn, ...(active ? s.roleBtnActive : {}) }}>
                      <span style={{ ...s.roleText, color: active ? colors.onBrandPrimary : colors.onSurface }}>
                        {r === "manager" ? "Manager" : r === "plant" ? "Plant User" : "Driver"}
                      </span>
                    </button>
                  );
                })}
              </div>
              {(role === "manager" || role === "plant") && (
                <>
                  <div style={s.label}>{role === "manager" ? "Manager invite code" : "Plant user invite code"}</div>
                  <input data-testid="input-manager-code" style={s.input} value={managerCode}
                    onChange={(e) => setManagerCode(e.target.value)} placeholder="Provided by your admin" />
                </>
              )}
            </div>
          )}

          <div style={s.label}>Email</div>
          <input data-testid="input-email" style={s.input} value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yardops.com" autoCapitalize="none" type="email" />

          <div style={s.label}>Password</div>
          <input data-testid="input-password" style={s.input} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" type="password" />

          {err && (
            <div style={s.errBox} data-testid="auth-error">
              <span style={s.errText}>{err}</span>
            </div>
          )}

          <button type="submit" data-testid="submit-button" disabled={busy}
            style={{ ...s.cta, ...(busy ? { opacity: 0.6 } : {}) }}>
            <span style={s.ctaText}>{busy ? "…" : mode === "login" ? "Sign in →" : "Create account →"}</span>
          </button>

          <div style={s.orRow}>
            <div style={s.orLine} />
            <span style={s.orText}>or</span>
            <div style={s.orLine} />
          </div>

          <button type="button" data-testid="google-signin" style={s.googleBtn} onClick={signInWithGoogle}>
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style={s.googleIcon} />
            <span style={s.googleText}>Continue with Google</span>
          </button>

          <div style={s.divider} />
          <div style={s.demoLabel}>Quick demo access</div>
          <div style={s.demoRow}>
            <button type="button" data-testid="demo-manager" style={s.demoBtn} onClick={() => fillDemo("manager")}>
              <span style={s.demoBtnText}>Use manager</span>
            </button>
            <button type="button" data-testid="demo-plant" style={s.demoBtn} onClick={() => fillDemo("plant")}>
              <span style={s.demoBtnText}>Use plant user</span>
            </button>
            <button type="button" data-testid="demo-driver" style={s.demoBtn} onClick={() => fillDemo("driver")}>
              <span style={s.demoBtnText}>Use driver</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", backgroundColor: colors.surface, display: "flex", alignItems: "center", justifyContent: "center", padding: spacing.lg, fontFamily: SERIF },
  card: { width: "100%", maxWidth: 460, backgroundColor: colors.surfaceSecondary, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface },
  hero: { paddingTop: spacing.xxl, paddingBottom: spacing.lg, borderBottom: `2px solid ${colors.onSurface}`, display: "flex", flexDirection: "column", alignItems: "center" },
  logo: { width: 240, height: 150, objectFit: "contain", marginBottom: spacing.md },
  brandTag: { color: colors.accent, ...fonts.monoBold, fontSize: 12, letterSpacing: 0 },
  formWrap: { padding: spacing.xl, display: "flex", flexDirection: "column" },
  tabs: { display: "flex", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, marginBottom: spacing.lg },
  tab: { flex: 1, padding: spacing.md, backgroundColor: colors.surfaceSecondary, border: "none", cursor: "pointer" },
  tabActive: { backgroundColor: colors.onSurface },
  tabText: { ...fonts.monoBold, fontSize: 13, letterSpacing: 0 },
  label: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: spacing.md, marginBottom: spacing.xs },
  input: {
    width: "100%", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary,
    padding: spacing.md, ...fonts.mono, fontSize: 16, color: colors.onSurface, outline: "none", boxSizing: "border-box",
  },
  roleRow: { display: "flex", gap: spacing.sm },
  roleBtn: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, padding: spacing.md, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  roleBtnActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  roleText: { ...fonts.monoBold, fontSize: 13, letterSpacing: 0 },
  cta: { backgroundColor: colors.brandPrimary, padding: 18, marginTop: spacing.lg, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, cursor: "pointer" },
  ctaText: { color: colors.onBrandPrimary, ...fonts.display, fontSize: 16, letterSpacing: 0 },
  errBox: { marginTop: spacing.md, backgroundColor: colors.error, padding: spacing.md, borderWidth: 2, borderStyle: "solid", borderColor: colors.error },
  errText: { color: colors.onError, ...fonts.monoBold, fontSize: 13 },
  divider: { height: 2, backgroundColor: colors.divider, margin: `${spacing.xl}px 0` },
  orRow: { display: "flex", alignItems: "center", gap: spacing.md, marginTop: spacing.lg },
  orLine: { flex: 1, height: 1, backgroundColor: colors.border },
  orText: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary },
  googleBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, marginTop: spacing.lg, padding: 16, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, cursor: "pointer", width: "100%" },
  googleIcon: { width: 20, height: 20 },
  googleText: { ...fonts.monoBold, fontSize: 14, color: colors.onSurface, letterSpacing: 0 },
  demoLabel: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceTertiary, marginBottom: spacing.sm, textAlign: "center" },
  demoRow: { display: "flex", gap: spacing.sm },
  demoBtn: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, padding: `${spacing.md}px 4px`, backgroundColor: colors.surfaceTertiary, cursor: "pointer" },
  demoBtnText: { ...fonts.monoBold, fontSize: 11, color: colors.onSurface, letterSpacing: 0 },
};
